import {
  DiscountedValue,
  DiscountSavingRow,
  MMButton,
  MMLabelValue,
  PreviewLayout,
  RecurringPreviewRow,
  SendMoneyAmountRow,
  TrustedRecipientCheckbox
} from '@components'
import WarningMessage from '@components/WarningMessage'
import { getAccountingCategoryLabel } from '@constants'
import { useFeatureFlag, useWithdrawPromoCode } from '@hooks'
import { formatCurrencyForDisplay } from '@utils'
import styles from '@withdrawals/styles/WithdrawPreview.module.scss'
import {
  useCurrencies,
  useFeatureFlagState,
  useMyAccountAccessQry,
  useWithdrawInitiateMutation,
  useWithdrawPreviewMutation
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse, RecipientResponse } from 'mmfintech-backend-api/types'
import { formatFloat, tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useState } from 'react'

type CryptoTransferPreviewProps = {
  selectedAccount: AccountBalanceResponse
  selectedRecipient: RecipientResponse
  onSubmit: () => void
  onCancel: () => void
  formValues: UseFormValuesOutput
  /** Optional callback when promo code is applied - can be sync or async */
  onApplyPromoCode?: (code: string) => void | Promise<void>
}

const CryptoTransferPreview = (props: CryptoTransferPreviewProps) => {
  const { selectedAccount, selectedRecipient, onSubmit, onCancel, formValues, onApplyPromoCode } = props

  const userSettingsInitiatorPermissions = useFeatureFlag('userSettingsInitiatorPermissions')

  const { hasRequestPermission, hasWritePermission } = useMyAccountAccessQry()
  const hasWithdrawPromoCode = useFeatureFlag('withdrawPromoCode')

  const [_, { data: withdrawPreview }] = useWithdrawPreviewMutation({
    fixedCacheKey: 'crypto-transfer-preview'
  })
  const [__, { error: withdrawError, isLoading: withdrawFetching }] = useWithdrawInitiateMutation({
    fixedCacheKey: 'crypto-transfer-initiate'
  })

  // Track promo code state for clear functionality
  const [promoCode, setPromoCode] = useState('')

  // Promo code validation hook (combines stable preview + error handling + validation logic)
  const {
    displayPreview,
    validatePromoCode,
    isLoading: isPromoLoading,
    errorMessage: promoErrorMessage
  } = useWithdrawPromoCode({
    mutation: useWithdrawPreviewMutation,
    subFlow: 'CRYPTO',
    previewData: withdrawPreview,
    buildRequest: (code: string) => {
      const allFormValues = formValues.prepare()
      return {
        ...allFormValues,
        amount: parseFloat(allFormValues.amount),
        sourceAccountId: selectedAccount?.id,
        destinationPaymentInstrumentId: selectedRecipient?.paymentInstrumentId,
        promoCode: code
      }
    },
    onSuccess: async (code: string) => {
      setPromoCode(code)
      if (onApplyPromoCode) {
        await onApplyPromoCode(code)
      }
    }
  })

  const { getCurrencyPrecision } = useCurrencies()

  // Use displayPreview for all UI rendering (stable, won't nullify on errors)
  const {
    fee,
    feeCurrency,
    // fxRate,
    // fxRateCurrency,
    processingAmount,
    processingCurrency,
    walletAmount,
    walletCurrency,
    trustedRecipient,
    originalFeeAmount,
    promoCodeDiscount
  } = displayPreview || {}

  const { name: accountName } = selectedAccount || {}

  const isRequestMode = hasRequestPermission(selectedAccount?.id) && !hasWritePermission(selectedAccount?.id)
  const showPromo = hasWithdrawPromoCode && !!onApplyPromoCode && !isRequestMode && Number(originalFeeAmount || fee) > 0
  const isPromoApplied = !!promoCode && Number(promoCodeDiscount) > 0

  const accountingCategoryFF = useFeatureFlagState('WITHDRAW_ACCOUNTING_CATEGORY')

  return (
    <PreviewLayout
      showPromo={showPromo}
      promoCodeConfig={{
        validatePromoCode,
        isLoading: isPromoLoading,
        error: promoErrorMessage,
        isApplied: isPromoApplied,
        onClear: () => setPromoCode('')
      }}
    >
      <div className={styles['withdraw-preview-wrapper']}>
        <div className='withdraw-preview-container amount-container'>
          <div className='main-amount-label'>{tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.AMOUNT', 'Amount')}</div>
          <div className='main-amount-value'>
            <span data-test='amount'>
              {formatFloat(walletAmount, getCurrencyPrecision(walletCurrency))}{' '}
              <span className='main-amount-currency'>{formatCurrencyForDisplay(walletCurrency)}</span>
            </span>
          </div>

          {(Number(originalFeeAmount) > 0 || fee > 0) && (
            <SendMoneyAmountRow
              label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.FEE', 'Fee')}
              value={
                <DiscountedValue
                  value={formatFloat(originalFeeAmount || fee, getCurrencyPrecision(feeCurrency))}
                  discountedValue={formatFloat(fee, getCurrencyPrecision(feeCurrency))}
                  showDiscount={!!originalFeeAmount && originalFeeAmount !== fee}
                  currency={feeCurrency}
                />
              }
              data-test='fee-amount'
            />
          )}

          {Number(promoCodeDiscount) > 0 && (
            <DiscountSavingRow
              value={formatFloat(promoCodeDiscount, getCurrencyPrecision(feeCurrency))}
              currency={feeCurrency}
            />
          )}

          <SendMoneyAmountRow
            label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.NET_AMOUNT', 'Net amount')}
            value={
              <span>
                {formatFloat(processingAmount, getCurrencyPrecision(processingCurrency))}{' '}
                <span className='row-amount-currency'>{formatCurrencyForDisplay(processingCurrency)}</span>
              </span>
            }
            data-test='net-amount'
          />
        </div>

        <div className='withdraw-preview-container send-money-details'>
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.FROM', 'From account')}
            value={accountName}
            data-test='account-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.TO', 'To')}
            value={selectedRecipient?.name}
            data-test='recipient-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.ADDRESS', 'Address')}
            value={selectedRecipient?.cryptoAddress}
            data-test='address'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.TAG', 'Tag')}
            value={selectedRecipient?.tag}
            data-test='tag'
          />

          {accountingCategoryFF.isEnabled && (
            <MMLabelValue
              variant='withdrawal'
              label={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.CATEGORY', 'Payment category')}
              value={getAccountingCategoryLabel(formValues.getValue('accountingCategory'))}
              data-test='accounting-category'
            />
          )}

          <RecurringPreviewRow formValues={formValues} />
        </div>

        <TrustedRecipientCheckbox formValues={formValues} trustedRecipient={trustedRecipient} />

        <WarningMessage
          title={tr('FRONTEND.WITHDRAW.CRYPTO.WARNING.PLEASE_NOTE', 'Please note')}
          lines={[
            tr('FRONTEND.WITHDRAW.CRYPTO.WARNING.LINE1', 'Please verify this information before confirmation.'),
            tr(
              'FRONTEND.WITHDRAW.CRYPTO.WARNING.LINE2',
              'Executed transactions cannot be cancelled or refunded. Sending coins to a wrong address may result in loss of funds.'
            )
          ]}
        />

        <PreviewLayout.MobilePromo />

        <ErrorDisplay error={withdrawError} />

        <div className='preview-actions'>
          <MMButton
            type='button'
            size='large'
            variant='alternative'
            caption={
              userSettingsInitiatorPermissions && isRequestMode
                ? tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.BUTTON_REQUEST', 'Request')
                : tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.BUTTON_SEND', 'Send')
            }
            onClick={onSubmit}
            loading={withdrawFetching}
            data-test='button-send'
            fullWidth
          />
          <MMButton
            type='button'
            size='large'
            variant='tertiary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onCancel}
            disabled={withdrawFetching}
            data-test='button-cancel'
            fullWidth
          />
        </div>
      </div>
    </PreviewLayout>
  )
}

export default CryptoTransferPreview
